import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';

import { ROUTES } from '../../../../constants/RouteNameMapping';
import { OriginViewDataModel, VIEW_TYPE } from '../../../../data-models/view-config.data-model';
import { getTrackableViewId } from '../../../../models/view.model';
import { PermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import { SelectItem } from '../../../../types';
import { trackEvent } from '../../../../util/datadogTracking';
import { useReorderViews } from '../../hooks/usePageActions';
import { pageConfigPFJ } from '../../state/PageStateJ';
import { selectedViewPFJ, viewsPFJ } from '../../state/ViewStateJ';

interface ViewNavigationInterface {
  items: SelectItem[];
  onReorder: (items: SelectItem[]) => void;
  onSelect?: (item: SelectItem) => void;
  selectedItem: SelectItem | null;
  isLoading: boolean;
  viewsMap: Record<string, OriginViewDataModel>;
  setIsItemDropdownOpen: Dispatch<SetStateAction<boolean>>;
  isItemDropdownOpen: boolean;
}

const ViewNavigationContext = createContext<ViewNavigationInterface | undefined>(undefined);

export const ViewNavigationProvider = ({ children }: { children?: ReactNode }) => {
  const navigate = useNavigate();
  const views = useAtomValue(viewsPFJ);
  const pageConfig = useAtomValue(pageConfigPFJ);
  const selectedView = useAtomValue(selectedViewPFJ);
  const reorderViews = useReorderViews();
  const hasAuditorAccess = PermissionService.get().hasPermission(PermissionKey.hasAuditorAccess);

  // Filter views for auditors - only show SHARED views
  const filteredViews = useMemo(() => {
    if (!hasAuditorAccess) {
      return views;
    }
    return views.filter((view) => view.type === VIEW_TYPE.SHARED);
  }, [views, hasAuditorAccess]);

  const viewsMap = useMemo(
    () =>
      filteredViews.reduce(
        (map, view) => ({ ...map, [view.viewId]: view }),
        {} as Record<string, OriginViewDataModel>
      ) ?? {},
    [filteredViews]
  );

  const [isItemDropdownOpen, setIsItemDropdownOpen] = useState(false);
  const { order: viewOrder } = pageConfig;
  const [items, setItems] = useState<SelectItem[]>(() => {
    return getViewOptions(viewsMap, viewOrder);
  });
  const [selectedItem, setSelectedItem] = useState<SelectItem | null>(() => {
    return items.find((item) => item.id === selectedView?.viewId) ?? null;
  });

  useEffect(() => {
    setItems(getViewOptions(viewsMap, viewOrder));
  }, [viewOrder, viewsMap]);

  const onSelect = useCallback(
    (item: SelectItem) => {
      setSelectedItem(item);

      const selectedViewFromItem = filteredViews.find((view) => view.viewId === item.id);

      if (selectedViewFromItem) {
        trackEvent('portfolio.view_opened', {
          view_id: getTrackableViewId(selectedViewFromItem),
          view_type: selectedViewFromItem.type,
          view_data_type: selectedViewFromItem.viewType,
        });
        navigate(`/${ROUTES.PORTFOLIO}/${selectedViewFromItem.id}`);
      }
    },
    [navigate, filteredViews]
  );

  const onReorder = useCallback(
    async (items: SelectItem[]) => {
      setItems(items);
      return await reorderViews(items.map((item) => item.id as string));
    },
    [reorderViews]
  );

  const value = useMemo(
    () => ({
      isLoading: !views || !pageConfig,
      selectedItem,
      items,
      onReorder,
      onSelect,
      viewsMap,
      setIsItemDropdownOpen,
      isItemDropdownOpen,
    }),
    [views, pageConfig, selectedItem, items, onReorder, onSelect, viewsMap, isItemDropdownOpen]
  );

  return <ViewNavigationContext.Provider value={value}>{children}</ViewNavigationContext.Provider>;
};

export const useViewNavigation = (): ViewNavigationInterface => {
  const context = useContext(ViewNavigationContext);

  if (context === undefined) {
    throw new Error('useViewNavigation cannot be used outside ViewNavigationProvider');
  }

  return context;
};

// FD-3357: default (company) views are user-reorderable, so both default and
// custom views follow their saved position in pageConfig.order. Defaults stay
// grouped ahead of custom views.
export function getViewOptions(viewMap: Record<string, OriginViewDataModel>, viewOrder: string[]) {
  const orderedIds = viewOrder.filter((viewId) => viewMap[viewId]);
  const isDefault = (viewId: string) => viewMap[viewId].type === VIEW_TYPE.COMPANY;
  const sortedIds = [...orderedIds.filter(isDefault), ...orderedIds.filter((id) => !isDefault(id))];

  return sortedIds.map((viewId) => ({
    value: viewMap[viewId].label,
    id: viewId,
    viewType: viewMap[viewId].viewType,
    isCompany: viewMap[viewId].type === VIEW_TYPE.COMPANY,
  }));
}
